/**
 * GameEditor Component
 * Form to create new games or edit existing ones
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Grid,
  Divider,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useGame, useCreateGame, useUpdateGame } from '../../hooks/useGames';
import { useContributors } from '../../hooks/useContributors';
import { Loading } from '../Common/Loading';
import { ErrorMessage } from '../Common/ErrorMessage';
import {
  LANGUAGES,
  SCORING_TYPES,
  SCORING_TYPE_LABELS,
  SCORING_TYPE_DESCRIPTIONS,
  RELEASE_CHANNELS,
  RELEASE_CHANNEL_LABELS,
  PHASE_END_TYPES,
  PHASE_END_TYPE_LABELS,
  PHASE_TRIGGERS,
  PHASE_TRIGGER_LABELS,
  ROUND_BEHAVIORS,
  ROUND_BEHAVIOR_LABELS,
  ENTRY_STYLES,
  ENTRY_STYLE_LABELS,
  ENTRY_STYLE_DESCRIPTIONS
} from '../../utils/constants';

// Normalize legacy scoring type values to current naming
const normalizeScoringType = (type) => {
  const legacyMapping = {
    'multiScorePlus': SCORING_TYPES.MULTI_ROUNDS_PLUS,
    'multiScore': SCORING_TYPES.MULTI_ROUNDS
  };
  return legacyMapping[type] || type;
};

export const GameEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = id && id !== 'new';

  const { data: game, isLoading, error } = useGame(isEditMode ? id : null);
  const createMutation = useCreateGame();
  const updateMutation = useUpdateGame();

  const [saveError, setSaveError] = useState('');
  const [scoringCategories, setScoringCategories] = useState([]);
  const [predefinedTeams, setPredefinedTeams] = useState([]);

  // Contributor selection state
  const [selectedContributor, setSelectedContributor] = useState({ id: null, handle: '' });
  const [contributorDialogOpen, setContributorDialogOpen] = useState(false);
  const [contributorSearch, setContributorSearch] = useState('');

  // Fetch contributors for selection dialog (only when dialog is open)
  const { data: contributorsData, isLoading: contributorsLoading } = useContributors({
    status: 'activated',
    search: contributorSearch || undefined,
    limit: 50
  });

  // Round configuration (for multiRounds/multiRounds+)
  const [roundConfig, setRoundConfig] = useState({
    titles: [],
    expanding: false,
    sequential: true,
    behavior: ROUND_BEHAVIORS.UP,
    configText: '',
    // Down behavior fields
    rangeStart: null,
    rangeEnd: null,
    // Up-Down behavior fields
    floor: 1,
    ceiling: 6,
    // Display template for non-up behaviors
    displayTemplate: ''
  });

  // Phase end configuration (for multiRounds+ only)
  const [phaseEndConfig, setPhaseEndConfig] = useState({
    type: PHASE_END_TYPES.END_GAME,
    trigger: PHASE_TRIGGERS.MANUAL,
    triggerAfterRounds: null,
    titles: [],
    expanding: false
  });

  // Tie-breaker configuration
  const [tieBreakerConfig, setTieBreakerConfig] = useState({
    enabled: false,
    groups: []
  });

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      gameCode: '',
      gameName: '',
      category: '',
      language: 'en',
      schemaVersion: '1',
      contributorId: null,
      releaseChannel: '',  // New games have no release channel until promoted
      isExpansion: false,
      isVariant: false,
      parentGameCode: '',
      originalGameCode: '',
      // gameData fields
      version: '1.0',
      setupText: '',
      minPlayers: 2,
      maxPlayers: 4,
      maxTeams: 0,
      fungibleTeams: false,
      teamPoolPoints: false,
      scoringType: SCORING_TYPES.SCORE,
      winCondition: 'high',
      entryStyle: ENTRY_STYLES.INLINE,
      expansionOrder: 0,
      variantBehavior: '',
      versionReleaseNotes: ''
    }
  });

  // Watch form values for conditional rendering
  const isExpansion = watch('isExpansion');
  const isVariant = watch('isVariant');
  const scoringType = watch('scoringType');

  // Load game data when editing
  useEffect(() => {
    if (game) {
      const gameData = typeof game.gameData === 'string'
        ? JSON.parse(game.gameData)
        : game.gameData || {};

      reset({
        gameCode: game.gameCode || '',
        gameName: game.gameName || '',
        category: game.category || '',
        language: game.language || 'en',
        schemaVersion: String(game.schemaVersion || 1),
        contributorId: game.contributorId || null,
        releaseChannel: game.releaseChannel || '',
        isExpansion: game.isExpansion || false,
        isVariant: game.isVariant || false,
        parentGameCode: game.parentGameCode || '',
        originalGameCode: game.originalGameCode || '',
        version: gameData.version || '1.0',
        setupText: gameData.setupText || '',
        minPlayers: gameData.minPlayers ?? 2,
        maxPlayers: gameData.maxPlayers ?? 4,
        maxTeams: gameData.maxTeams ?? 0,
        fungibleTeams: gameData.fungibleTeams || false,
        teamPoolPoints: gameData.teamPoolPoints || false,
        scoringType: normalizeScoringType(gameData.scoringType) || SCORING_TYPES.SCORE,
        winCondition: gameData.winCondition || 'high',
        entryStyle: gameData.entryStyle || ENTRY_STYLES.INLINE,
        expansionOrder: gameData.expansionOrder ?? 0,
        variantBehavior: gameData.variantBehavior || '',
        versionReleaseNotes: game.versionReleaseNotes || ''
      });

      // Load scoring categories with migration from boolean flags to contexts
      const categories = (gameData.scoringCategories || []).map(cat => {
        // If contexts array exists, use it directly
        if (cat.contexts) {
          return { ...cat };
        }
        // Migrate from boolean flags to contexts array
        const contexts = [];
        if (cat.showInRounds !== false) contexts.push('round');
        if (cat.showInPhaseEnd === true) contexts.push('phaseEnd');
        // Default to ['round'] if empty
        if (contexts.length === 0) contexts.push('round');
        // Return migrated category (exclude old boolean flags)
        const { showInRounds, showInPhaseEnd, ...rest } = cat;
        return { ...rest, contexts };
      });
      setScoringCategories(categories);
      setPredefinedTeams(gameData.predefinedTeams || []);

      // Set contributor selection state
      if (game.contributorId || game.contributorHandle) {
        setSelectedContributor({
          id: game.contributorId || null,
          handle: game.contributorHandle || ''
        });
      }

      // Load roundConfig (handle legacy properties for backward compatibility)
      if (gameData.roundConfig) {
        setRoundConfig({
          titles: gameData.roundConfig.titles || [],
          expanding: gameData.roundConfig.expanding || false,
          sequential: gameData.roundConfig.sequential !== false,
          behavior: gameData.roundConfig.behavior || ROUND_BEHAVIORS.UP,
          configText: gameData.roundConfig.configText || '',
          rangeStart: gameData.roundConfig.rangeStart ?? null,
          rangeEnd: gameData.roundConfig.rangeEnd ?? null,
          floor: gameData.roundConfig.floor ?? 1,
          ceiling: gameData.roundConfig.ceiling ?? 6,
          displayTemplate: gameData.roundConfig.displayTemplate || ''
        });
      } else {
        // Legacy format migration
        setRoundConfig({
          titles: gameData.scoringRoundTitles || [],
          expanding: gameData.configurableRounds || false,
          sequential: gameData.roundsSequential !== false,
          behavior: gameData.roundBehavior || ROUND_BEHAVIORS.UP,
          configText: gameData.roundConfigText || '',
          rangeStart: null,
          rangeEnd: null,
          floor: 1,
          ceiling: 6,
          displayTemplate: ''
        });
      }

      // Load phaseEndConfig
      if (gameData.phaseEndConfig) {
        setPhaseEndConfig({
          type: gameData.phaseEndConfig.type || PHASE_END_TYPES.END_GAME,
          trigger: gameData.phaseEndConfig.trigger || PHASE_TRIGGERS.MANUAL,
          triggerAfterRounds: gameData.phaseEndConfig.triggerAfterRounds || null,
          titles: gameData.phaseEndConfig.titles || [],
          expanding: gameData.phaseEndConfig.expanding || false
        });
      }

      // Load tieBreakerConfig
      if (gameData.tieBreakerConfig) {
        setTieBreakerConfig({
          enabled: gameData.tieBreakerConfig.enabled || false,
          groups: gameData.tieBreakerConfig.groups || []
        });
      }
    }
  }, [game, reset]);

  const onSubmit = async (formData) => {
    setSaveError('');

    try {
      // Build gameData object - only include fields with actual values
      // (does NOT include gameCode/gameName - those are separate DB columns)
      const gameData = {};

      // Required fields (always include)
      gameData.version = formData.version;
      gameData.scoringType = formData.scoringType;
      gameData.minPlayers = parseInt(formData.minPlayers);
      gameData.maxPlayers = parseInt(formData.maxPlayers);
      gameData.winCondition = formData.winCondition;

      // Entry style - only include if not default (inline) and not winLoss/streamEvents
      // Note: streamEvents always uses popup-style entry, but it's implicit
      if (formData.scoringType !== SCORING_TYPES.WIN_LOSS &&
          formData.scoringType !== SCORING_TYPES.STREAM_EVENTS &&
          formData.entryStyle && formData.entryStyle !== ENTRY_STYLES.INLINE) {
        gameData.entryStyle = formData.entryStyle;
      }

      // Optional fields - only include if they have content
      if (formData.setupText) gameData.setupText = formData.setupText;
      if (parseInt(formData.maxTeams) > 0) gameData.maxTeams = parseInt(formData.maxTeams);
      if (formData.fungibleTeams) gameData.fungibleTeams = true;
      if (formData.teamPoolPoints) gameData.teamPoolPoints = true;
      // Expansion-only fields
      if (formData.isExpansion && parseInt(formData.expansionOrder) > 0) {
        gameData.expansionOrder = parseInt(formData.expansionOrder);
      }
      // Expansion or Variant fields
      if ((formData.isExpansion || formData.isVariant) && formData.variantBehavior) {
        gameData.variantBehavior = formData.variantBehavior;
      }

      // Add scoring categories if present - clean up to only include non-default values
      if (scoringCategories.length > 0) {
        gameData.scoringCategories = scoringCategories.map(cat => {
          const cleaned = {
            id: cat.id,
            title: cat.title
          };
          if (cat.formula) cleaned.formula = cat.formula;
          if (cat.action && cat.action !== 'append') cleaned.action = cat.action;
          if (cat.selectable !== undefined) cleaned.selectable = cat.selectable;
          if (cat.dependsOn) cleaned.dependsOn = cat.dependsOn;
          // Contexts array - only include if not default (['round'])
          if (cat.contexts && cat.contexts.length > 0) {
            // Only include if different from default (just 'round')
            const isDefault = cat.contexts.length === 1 && cat.contexts[0] === 'round';
            if (!isDefault) {
              cleaned.contexts = cat.contexts;
            }
          }
          // Matrix layout labels (for popup entry style) - only include if both are set
          if (cat.rowLabel && cat.columnLabel) {
            cleaned.rowLabel = cat.rowLabel;
            cleaned.columnLabel = cat.columnLabel;
          }
          // Tap-to-add - only include if true and category has event context
          if (cat.tapToAdd && cat.contexts?.includes('event')) {
            cleaned.tapToAdd = true;
          }
          return cleaned;
        });
      }

      // Add predefined teams if present
      if (predefinedTeams.length > 0) {
        gameData.predefinedTeams = predefinedTeams;
      }

      // Add roundConfig for multiScore/multiScorePlus - only include non-default values
      if (formData.scoringType === SCORING_TYPES.MULTI_ROUNDS ||
          formData.scoringType === SCORING_TYPES.MULTI_ROUNDS_PLUS) {
        const rc = {};
        if (roundConfig.titles.length > 0) rc.titles = roundConfig.titles;
        if (roundConfig.expanding) rc.expanding = true;
        if (!roundConfig.sequential) rc.sequential = false;  // Only include if not default (true)
        if (roundConfig.behavior !== ROUND_BEHAVIORS.UP) rc.behavior = roundConfig.behavior;
        if (roundConfig.configText) rc.configText = roundConfig.configText;

        // Down behavior fields
        if (roundConfig.behavior === ROUND_BEHAVIORS.DOWN) {
          if (roundConfig.rangeStart != null) rc.rangeStart = roundConfig.rangeStart;
          if (roundConfig.rangeEnd != null) rc.rangeEnd = roundConfig.rangeEnd;
        }

        // Up-Down behavior fields
        if (roundConfig.behavior === ROUND_BEHAVIORS.UP_DOWN) {
          if (roundConfig.floor != null && roundConfig.floor !== 1) rc.floor = roundConfig.floor;
          if (roundConfig.ceiling != null && roundConfig.ceiling !== 6) rc.ceiling = roundConfig.ceiling;
        }

        // Display template for non-up behaviors
        if (roundConfig.behavior !== ROUND_BEHAVIORS.UP && roundConfig.displayTemplate) {
          rc.displayTemplate = roundConfig.displayTemplate;
        }

        if (Object.keys(rc).length > 0) gameData.roundConfig = rc;
      }

      // Add phaseEndConfig for multiScorePlus - always include type, other fields optional
      if (formData.scoringType === SCORING_TYPES.MULTI_ROUNDS_PLUS) {
        const pec = {
          type: phaseEndConfig.type || PHASE_END_TYPES.END_GAME  // Always include type
        };
        if (phaseEndConfig.trigger !== PHASE_TRIGGERS.MANUAL) pec.trigger = phaseEndConfig.trigger;
        if (phaseEndConfig.titles.length > 0) pec.titles = phaseEndConfig.titles;
        if (phaseEndConfig.expanding) pec.expanding = true;
        if (phaseEndConfig.trigger === PHASE_TRIGGERS.AUTOMATIC && phaseEndConfig.triggerAfterRounds) {
          pec.triggerAfterRounds = phaseEndConfig.triggerAfterRounds;
        }
        gameData.phaseEndConfig = pec;  // Always include for multiRounds+
      }

      // Add tieBreakerConfig - only include if enabled with groups
      if (tieBreakerConfig.enabled && tieBreakerConfig.groups.length > 0) {
        gameData.tieBreakerConfig = {
          enabled: true,
          groups: tieBreakerConfig.groups.map(group => ({
            id: group.id,
            title: group.title,
            winCondition: group.winCondition,
            questions: group.questions.map(q => ({
              id: q.id,
              title: q.title,
              formula: q.formula || '*1'
            }))
          }))
        };
      }

      // Build payload
      const payload = {
        gameCode: formData.gameCode,
        gameName: formData.gameName,
        category: formData.category,
        language: formData.language,
        schemaVersion: parseInt(formData.schemaVersion),
        contributorId: selectedContributor.id || null,
        releaseChannel: formData.releaseChannel || null,
        isExpansion: formData.isExpansion,
        isVariant: formData.isVariant,
        // Only include parentGameCode if isExpansion is checked
        parentGameCode: formData.isExpansion ? (formData.parentGameCode || null) : null,
        // Only include originalGameCode if isVariant is checked
        originalGameCode: formData.isVariant ? (formData.originalGameCode || null) : null,
        versionReleaseNotes: formData.versionReleaseNotes || null,
        gameData: gameData  // Send as object, not stringified
      };

      if (isEditMode) {
        await updateMutation.mutateAsync({ id: parseInt(id), data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      navigate('/games');
    } catch (err) {
      // Extract detailed validation errors if available
      const errorMessage = err.response?.data?.details
        ? err.response.data.details.join(', ')
        : err.response?.data?.message || err.message || 'Failed to save game';

      setSaveError(errorMessage);
      console.error('Save error:', err.response?.data);
    }
  };

  // Available contexts based on scoring type
  // Note: 'event' context enables hybrid event scoring (Quacks-style) where categories
  // can have both round scores and ad-hoc events
  const getAvailableContexts = () => {
    switch (scoringType) {
      case SCORING_TYPES.SCORE:
        return ['round', 'event'];  // Can have events even in simple scoring
      case SCORING_TYPES.MULTI_ROUNDS:
        return ['round', 'event', 'tieBreaker'];
      case SCORING_TYPES.MULTI_ROUNDS_PLUS:
        return ['round', 'phaseEnd', 'gameEnd', 'event', 'tieBreaker'];
      case SCORING_TYPES.STREAM_EVENTS:
        return ['event'];  // Stream events only have event context
      default:
        return ['round'];
    }
  };

  // Context display labels
  const CONTEXT_LABELS = {
    round: 'Round',
    phaseEnd: 'Phase',
    gameEnd: 'End',
    tieBreaker: 'Tie',
    event: 'Event'
  };

  // Context descriptions for tooltips
  const CONTEXT_DESCRIPTIONS = {
    round: 'Scored each round',
    phaseEnd: 'Scored at end of each phase',
    gameEnd: 'Scored once at game end',
    tieBreaker: 'Used for tie-breaker calculations',
    event: 'Ad-hoc events (bonus chips, special abilities, etc.)'
  };

  // Scoring category management
  const addScoringCategory = () => {
    // Default context depends on scoring type
    const defaultContexts = scoringType === SCORING_TYPES.STREAM_EVENTS
      ? ['event']  // Stream events: default to event context
      : ['round']; // All others: default to round context

    setScoringCategories([...scoringCategories, {
      id: '',
      title: '',
      formula: '',
      action: 'append',
      selectable: undefined,
      dependsOn: '',
      contexts: defaultContexts,
      rowLabel: '',         // Matrix layout (popup mode): group row label
      columnLabel: '',      // Matrix layout (popup mode): column header
      tapToAdd: false       // Tap-to-add: tap adds event directly without data entry
    }]);
  };

  const updateScoringCategory = (index, field, value) => {
    const updated = [...scoringCategories];
    updated[index][field] = value;
    setScoringCategories(updated);
  };

  const removeScoringCategory = (index) => {
    setScoringCategories(scoringCategories.filter((_, i) => i !== index));
  };

  // Drag and drop handlers for scoring categories
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/html'));

    if (dragIndex === dropIndex) return;

    const updated = [...scoringCategories];
    const [removed] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, removed);
    setScoringCategories(updated);
  };

  // Round config helpers
  const updateRoundConfigField = (field, value) => {
    setRoundConfig(prev => ({ ...prev, [field]: value }));
  };

  const addRoundTitle = () => {
    if (roundConfig.expanding && roundConfig.titles.length >= 1) return;
    setRoundConfig(prev => ({ ...prev, titles: [...prev.titles, ''] }));
  };

  const updateRoundTitle = (index, value) => {
    const updated = [...roundConfig.titles];
    updated[index] = value;
    setRoundConfig(prev => ({ ...prev, titles: updated }));
  };

  const removeRoundTitle = (index) => {
    setRoundConfig(prev => ({
      ...prev,
      titles: prev.titles.filter((_, i) => i !== index)
    }));
  };

  // Phase end config helpers
  const updatePhaseEndConfigField = (field, value) => {
    setPhaseEndConfig(prev => ({ ...prev, [field]: value }));
  };

  const addPhaseTitle = () => {
    if (phaseEndConfig.expanding && phaseEndConfig.titles.length >= 1) return;
    setPhaseEndConfig(prev => ({ ...prev, titles: [...prev.titles, ''] }));
  };

  const updatePhaseTitle = (index, value) => {
    const updated = [...phaseEndConfig.titles];
    updated[index] = value;
    setPhaseEndConfig(prev => ({ ...prev, titles: updated }));
  };

  const removePhaseTitle = (index) => {
    setPhaseEndConfig(prev => ({
      ...prev,
      titles: prev.titles.filter((_, i) => i !== index)
    }));
  };

  // Tie-breaker config helpers
  const addTieBreakerGroup = () => {
    const newGroup = {
      id: `group-${Date.now()}`,
      title: '',
      winCondition: 'high',
      questions: [{
        id: `question-${Date.now()}`,
        title: '',
        formula: '*1'
      }]
    };
    setTieBreakerConfig(prev => ({
      ...prev,
      groups: [...prev.groups, newGroup]
    }));
  };

  const updateTieBreakerGroup = (groupIndex, field, value) => {
    setTieBreakerConfig(prev => ({
      ...prev,
      groups: prev.groups.map((g, i) =>
        i === groupIndex ? { ...g, [field]: value } : g
      )
    }));
  };

  const removeTieBreakerGroup = (groupIndex) => {
    setTieBreakerConfig(prev => ({
      ...prev,
      groups: prev.groups.filter((_, i) => i !== groupIndex)
    }));
  };

  const addTieBreakerQuestion = (groupIndex) => {
    const newQuestion = {
      id: `question-${Date.now()}`,
      title: '',
      formula: '*1'
    };
    setTieBreakerConfig(prev => ({
      ...prev,
      groups: prev.groups.map((g, i) =>
        i === groupIndex
          ? { ...g, questions: [...g.questions, newQuestion] }
          : g
      )
    }));
  };

  const updateTieBreakerQuestion = (groupIndex, questionIndex, field, value) => {
    setTieBreakerConfig(prev => ({
      ...prev,
      groups: prev.groups.map((g, i) =>
        i === groupIndex
          ? {
              ...g,
              questions: g.questions.map((q, qi) =>
                qi === questionIndex ? { ...q, [field]: value } : q
              )
            }
          : g
      )
    }));
  };

  const removeTieBreakerQuestion = (groupIndex, questionIndex) => {
    setTieBreakerConfig(prev => ({
      ...prev,
      groups: prev.groups.map((g, i) =>
        i === groupIndex
          ? { ...g, questions: g.questions.filter((_, qi) => qi !== questionIndex) }
          : g
      )
    }));
  };

  // Predefined teams management
  const addTeam = () => {
    setPredefinedTeams([...predefinedTeams, '']);
  };

  const updateTeam = (index, value) => {
    const updated = [...predefinedTeams];
    updated[index] = value;
    setPredefinedTeams(updated);
  };

  const removeTeam = (index) => {
    setPredefinedTeams(predefinedTeams.filter((_, i) => i !== index));
  };

  if (isLoading) return <Loading message="Loading game..." />;
  if (error && isEditMode) return <ErrorMessage error={error} />;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/games')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4">
            {isEditMode ? 'Edit Game' : 'Create New Game'}
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>
          {saveError}
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Column 1: Basic Information */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
              <Divider sx={{ mb: 2 }} />

              <Controller
                name="gameCode"
                control={control}
                rules={{ required: 'Game code is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Game Code"
                    fullWidth
                    margin="normal"
                    error={!!errors.gameCode}
                    helperText={errors.gameCode?.message}
                    disabled={isEditMode}
                  />
                )}
              />

              <Controller
                name="gameName"
                control={control}
                rules={{ required: 'Game name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Game Name"
                    fullWidth
                    margin="normal"
                    error={!!errors.gameName}
                    helperText={errors.gameName?.message}
                  />
                )}
              />

              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Category"
                    fullWidth
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="language"
                control={control}
                rules={{ required: 'Language is required' }}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal" error={!!errors.language}>
                    <InputLabel>Language</InputLabel>
                    <Select {...field} label="Language">
                      {LANGUAGES.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="schemaVersion"
                control={control}
                rules={{
                  required: 'Schema version is required',
                  pattern: {
                    value: /^\d+$/,
                    message: 'Schema version must be an integer'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Schema Version"
                    type="number"
                    fullWidth
                    margin="normal"
                    error={!!errors.schemaVersion}
                    helperText={errors.schemaVersion?.message}
                    inputProps={{ step: 1 }}
                  />
                )}
              />

              <Controller
                name="version"
                control={control}
                rules={{
                  required: 'Game version is required',
                  pattern: {
                    value: /^\d+\.\d+$/,
                    message: 'Version must be in major.minor format (e.g., 1.0)'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Game Version"
                    fullWidth
                    margin="normal"
                    placeholder="1.0"
                    error={!!errors.version}
                    helperText={errors.version?.message}
                  />
                )}
              />

              {/* Contributor Selection */}
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Contributor
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                  {selectedContributor.id ? (
                    <>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedContributor.handle || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {selectedContributor.id}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedContributor({ id: null, handle: '' })}
                        title="Clear contributor"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      No contributor selected
                    </Typography>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setContributorDialogOpen(true)}
                  >
                    {selectedContributor.id ? 'Change' : 'Select'}
                  </Button>
                </Box>
              </Box>

              <Controller
                name="releaseChannel"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Release Channel</InputLabel>
                    <Select {...field} label="Release Channel">
                      <MenuItem value="">
                        <em>None (not in release flow)</em>
                      </MenuItem>
                      {Object.values(RELEASE_CHANNELS).map((channel) => (
                        <MenuItem key={channel} value={channel}>
                          {RELEASE_CHANNEL_LABELS[channel]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="versionReleaseNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Version Release Notes"
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                  />
                )}
              />
            </Paper>

            {/* Game Type & Relations */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Game Type & Relations</Typography>
              <Divider sx={{ mb: 2 }} />

              <Controller
                name="isExpansion"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Is Expansion"
                  />
                )}
              />

              <Controller
                name="isVariant"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Is Variant"
                  />
                )}
              />

              {isExpansion && (
                <>
                  <Controller
                    name="parentGameCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Parent Game Code"
                        fullWidth
                        margin="normal"
                      />
                    )}
                  />

                  <Controller
                    name="expansionOrder"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Expansion Order"
                        type="number"
                        fullWidth
                        margin="normal"
                      />
                    )}
                  />
                </>
              )}

              {isVariant && (
                <Controller
                  name="originalGameCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Original Game Code"
                      fullWidth
                      margin="normal"
                    />
                  )}
                />
              )}

              {(isExpansion || isVariant) && (
                <Controller
                  name="variantBehavior"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Variant Behavior</InputLabel>
                      <Select {...field} label="Variant Behavior">
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="append">Append</MenuItem>
                        <MenuItem value="replace">Replace</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              )}
            </Paper>
          </Grid>

          {/* Column 2: Player & Scoring Configuration */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Player Configuration</Typography>
              <Divider sx={{ mb: 2 }} />

              <Controller
                name="minPlayers"
                control={control}
                rules={{ required: 'Min players is required', min: 1 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Min Players"
                    type="number"
                    fullWidth
                    margin="normal"
                    error={!!errors.minPlayers}
                    helperText={errors.minPlayers?.message}
                  />
                )}
              />

              <Controller
                name="maxPlayers"
                control={control}
                rules={{ required: 'Max players is required', min: 1 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Max Players"
                    type="number"
                    fullWidth
                    margin="normal"
                    error={!!errors.maxPlayers}
                    helperText={errors.maxPlayers?.message}
                  />
                )}
              />

              <Controller
                name="maxTeams"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Max Teams"
                    type="number"
                    fullWidth
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="fungibleTeams"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Fungible Teams"
                  />
                )}
              />

              <Controller
                name="teamPoolPoints"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Team Pool Points"
                  />
                )}
              />

              {/* Predefined Teams */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Predefined Teams
                </Typography>
                {predefinedTeams.map((team, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      value={team}
                      onChange={(e) => updateTeam(idx, e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="Team name"
                    />
                    <IconButton size="small" onClick={() => removeTeam(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addTeam}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Add Team
                </Button>
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Scoring Configuration</Typography>
              <Divider sx={{ mb: 2 }} />

              <Controller
                name="scoringType"
                control={control}
                rules={{ required: 'Scoring type is required' }}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal" error={!!errors.scoringType}>
                    <InputLabel>Scoring Type</InputLabel>
                    <Select {...field} label="Scoring Type">
                      {Object.values(SCORING_TYPES).map((type) => (
                        <MenuItem key={type} value={type}>
                          <Box>
                            <Typography variant="body1">{SCORING_TYPE_LABELS[type]}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {SCORING_TYPE_DESCRIPTIONS[type]}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="winCondition"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Win Condition</InputLabel>
                    <Select {...field} label="Win Condition">
                      <MenuItem value="high">High Score Wins</MenuItem>
                      <MenuItem value="low">Low Score Wins</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              {/* Entry Style - only for non-winLoss and non-streamEvents scoring types */}
              {scoringType !== SCORING_TYPES.WIN_LOSS && scoringType !== SCORING_TYPES.STREAM_EVENTS && (
                <Controller
                  name="entryStyle"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Entry Style</InputLabel>
                      <Select {...field} label="Entry Style">
                        {Object.entries(ENTRY_STYLE_LABELS).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label} - {ENTRY_STYLE_DESCRIPTIONS[value]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              )}
              {/* Note about event entry */}
              {scoringType === SCORING_TYPES.STREAM_EVENTS && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Stream Events always use popup-style entry for adding events.
                </Alert>
              )}

            </Paper>
          </Grid>

          {/* Column 3: Advanced Configuration */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Setup Instructions</Typography>
              <Divider sx={{ mb: 2 }} />

              <Controller
                name="setupText"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Setup Text"
                    fullWidth
                    multiline
                    rows={6}
                    margin="normal"
                    helperText="Supports markdown: **bold**, *italic*, `code`, [link](url). Line breaks preserved."
                  />
                )}
              />
            </Paper>

            {/* Stream Events Info */}
            {scoringType === SCORING_TYPES.STREAM_EVENTS && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50' }}>
                <Typography variant="h6" gutterBottom>Stream Events Scoring</Typography>
                <Divider sx={{ mb: 2 }} />
                <Alert severity="info">
                  Stream Events scoring is for games like Space Base where scores come from a continuous
                  flow of events rather than structured rounds. All categories should use the "Event" context.
                  Players can add events at any time during the game.
                </Alert>
              </Paper>
            )}

            {/* Round Configuration */}
            {(scoringType === SCORING_TYPES.MULTI_ROUNDS || scoringType === SCORING_TYPES.MULTI_ROUNDS_PLUS) && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Round Configuration</Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={roundConfig.sequential}
                      onChange={(e) => updateRoundConfigField('sequential', e.target.checked)}
                    />
                  }
                  label="Sequential Rounds"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={roundConfig.expanding}
                      onChange={(e) => updateRoundConfigField('expanding', e.target.checked)}
                    />
                  }
                  label="Expanding Rounds (unlimited)"
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Round Behavior</InputLabel>
                  <Select
                    value={roundConfig.behavior}
                    onChange={(e) => updateRoundConfigField('behavior', e.target.value)}
                    label="Round Behavior"
                  >
                    {Object.values(ROUND_BEHAVIORS).map((behavior) => (
                      <MenuItem key={behavior} value={behavior}>
                        {ROUND_BEHAVIOR_LABELS[behavior]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Down behavior fields */}
                {roundConfig.behavior === ROUND_BEHAVIORS.DOWN && (
                  <>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <TextField
                        type="number"
                        value={roundConfig.rangeStart ?? ''}
                        onChange={(e) => updateRoundConfigField('rangeStart', e.target.value ? parseInt(e.target.value) : null)}
                        label="Range Start"
                        helperText="Starting round value (e.g., 15)"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        type="number"
                        value={roundConfig.rangeEnd ?? ''}
                        onChange={(e) => updateRoundConfigField('rangeEnd', e.target.value ? parseInt(e.target.value) : null)}
                        label="Range End"
                        helperText="Ending round value (e.g., 0)"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    {roundConfig.rangeStart != null && roundConfig.rangeEnd != null && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Preview: {(() => {
                          const start = roundConfig.rangeStart;
                          const end = roundConfig.rangeEnd;
                          const totalRounds = start - end + 1;
                          const preview = totalRounds <= 8
                            ? Array.from({ length: totalRounds }, (_, i) => start - i).join(', ')
                            : `${start}, ${start-1}, ${start-2}, ..., ${end+2}, ${end+1}, ${end}`;
                          return `${preview} (${totalRounds} rounds)`;
                        })()}
                      </Alert>
                    )}
                  </>
                )}

                {/* Up-Down behavior fields */}
                {roundConfig.behavior === ROUND_BEHAVIORS.UP_DOWN && (
                  <>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <TextField
                        type="number"
                        value={roundConfig.floor ?? 1}
                        onChange={(e) => updateRoundConfigField('floor', parseInt(e.target.value) || 1)}
                        label="Floor"
                        helperText="Minimum value (default: 1)"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        type="number"
                        value={roundConfig.ceiling ?? 6}
                        onChange={(e) => updateRoundConfigField('ceiling', parseInt(e.target.value) || 6)}
                        label="Ceiling"
                        helperText="Maximum value (default: 6)"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Preview: {(() => {
                        const floor = roundConfig.floor || 1;
                        const ceiling = roundConfig.ceiling || 6;
                        const up = Array.from({ length: ceiling - floor + 1 }, (_, i) => floor + i);
                        const down = Array.from({ length: ceiling - floor }, (_, i) => ceiling - 1 - i);
                        const totalRounds = (ceiling - floor) * 2 + 1;
                        return `${[...up, ...down].join(', ')} (${totalRounds} rounds)`;
                      })()}
                    </Alert>
                  </>
                )}

                {/* Display template for non-up behaviors */}
                {roundConfig.behavior !== ROUND_BEHAVIORS.UP && (
                  <TextField
                    value={roundConfig.displayTemplate}
                    onChange={(e) => updateRoundConfigField('displayTemplate', e.target.value)}
                    label="Display Template"
                    helperText="Format for round titles (e.g., 'Double {n}' → 'Double 15')"
                    fullWidth
                    margin="normal"
                  />
                )}

                <TextField
                  value={roundConfig.configText}
                  onChange={(e) => updateRoundConfigField('configText', e.target.value)}
                  label="Round Configuration Text"
                  helperText="Help text shown in game setup UI"
                  fullWidth
                  multiline
                  rows={2}
                  margin="normal"
                />

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Round Titles {roundConfig.expanding && '(Template)'}
                </Typography>
                {roundConfig.expanding && roundConfig.titles.length >= 1 && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Expanding mode: single title used as template (e.g., "Round" → "Round 1", "Round 2"...)
                  </Alert>
                )}
                {roundConfig.titles.map((title, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      value={title}
                      onChange={(e) => updateRoundTitle(idx, e.target.value)}
                      size="small"
                      fullWidth
                      placeholder={roundConfig.expanding ? 'Template (e.g., Round)' : `Round ${idx + 1}`}
                    />
                    <IconButton size="small" onClick={() => removeRoundTitle(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addRoundTitle}
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={roundConfig.expanding && roundConfig.titles.length >= 1}
                >
                  Add Round Title
                </Button>
              </Paper>
            )}

            {/* Phase End Configuration (multiScorePlus only) */}
            {scoringType === SCORING_TYPES.MULTI_ROUNDS_PLUS && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Phase End Configuration</Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Phase Type</InputLabel>
                  <Select
                    value={phaseEndConfig.type}
                    onChange={(e) => updatePhaseEndConfigField('type', e.target.value)}
                    label="Phase Type"
                  >
                    {Object.values(PHASE_END_TYPES).map((type) => (
                      <MenuItem key={type} value={type}>
                        {PHASE_END_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Trigger</InputLabel>
                  <Select
                    value={phaseEndConfig.trigger}
                    onChange={(e) => updatePhaseEndConfigField('trigger', e.target.value)}
                    label="Trigger"
                  >
                    {Object.values(PHASE_TRIGGERS).map((trigger) => (
                      <MenuItem key={trigger} value={trigger}>
                        {PHASE_TRIGGER_LABELS[trigger]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {phaseEndConfig.trigger === PHASE_TRIGGERS.AUTOMATIC && (
                  <TextField
                    type="number"
                    value={phaseEndConfig.triggerAfterRounds || ''}
                    onChange={(e) => updatePhaseEndConfigField('triggerAfterRounds', parseInt(e.target.value) || null)}
                    label="Trigger After N Rounds"
                    fullWidth
                    margin="normal"
                    inputProps={{ min: 1 }}
                  />
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={phaseEndConfig.expanding}
                      onChange={(e) => updatePhaseEndConfigField('expanding', e.target.checked)}
                    />
                  }
                  label="Expanding Phases (unlimited)"
                />

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Phase Titles {phaseEndConfig.expanding && '(Template)'}
                </Typography>
                {phaseEndConfig.expanding && phaseEndConfig.titles.length >= 1 && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Expanding mode: single title used as template (e.g., "Age" → "Age 1", "Age 2"...)
                  </Alert>
                )}
                {phaseEndConfig.titles.map((title, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      value={title}
                      onChange={(e) => updatePhaseTitle(idx, e.target.value)}
                      size="small"
                      fullWidth
                      placeholder={phaseEndConfig.expanding ? 'Template (e.g., Age)' : `Phase ${idx + 1}`}
                    />
                    <IconButton size="small" onClick={() => removePhaseTitle(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addPhaseTitle}
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={phaseEndConfig.expanding && phaseEndConfig.titles.length >= 1}
                >
                  Add Phase Title
                </Button>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Scoring Categories - Full Width */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>Scoring Categories</Typography>
          <Divider sx={{ mb: 2 }} />

          {scoringCategories.map((category, idx) => (
            <Box
              key={idx}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, idx)}
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                cursor: 'move',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs="auto">
                  <DragIcon sx={{ color: 'text.secondary', cursor: 'grab' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="ID"
                    value={category.id}
                    onChange={(e) => updateScoringCategory(idx, 'id', e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Title"
                    value={category.title}
                    onChange={(e) => updateScoringCategory(idx, 'title', e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Formula"
                    value={category.formula}
                    onChange={(e) => updateScoringCategory(idx, 'formula', e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={category.action || 'append'}
                      onChange={(e) => updateScoringCategory(idx, 'action', e.target.value)}
                      label="Action"
                    >
                      <MenuItem value="append">Append</MenuItem>
                      <MenuItem value="replace">Replace</MenuItem>
                      <MenuItem value="remove">Remove</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Selectable</InputLabel>
                    <Select
                      value={category.selectable === undefined ? 'nil' : category.selectable.toString()}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateScoringCategory(idx, 'selectable',
                          val === 'nil' ? undefined : val === 'true'
                        );
                      }}
                      label="Selectable"
                    >
                      <MenuItem value="nil">Always On</MenuItem>
                      <MenuItem value="true">Default Selected</MenuItem>
                      <MenuItem value="false">Default Not Selected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={1}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Depends On</InputLabel>
                    <Select
                      value={category.dependsOn || ''}
                      onChange={(e) => updateScoringCategory(idx, 'dependsOn', e.target.value)}
                      label="Depends On"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {scoringCategories
                        .filter((cat, catIdx) => catIdx !== idx && cat.id)
                        .map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.id} {cat.title ? `(${cat.title})` : ''}
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
                {/* Context Matrix - show checkboxes for available contexts based on scoring type */}
                {getAvailableContexts().length > 1 && (
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                        Contexts
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getAvailableContexts().map(ctx => (
                          <Chip
                            key={ctx}
                            label={CONTEXT_LABELS[ctx]}
                            size="small"
                            variant={category.contexts?.includes(ctx) ? 'filled' : 'outlined'}
                            color={category.contexts?.includes(ctx) ? 'primary' : 'default'}
                            onClick={() => {
                              const currentContexts = category.contexts || ['round'];
                              const hasContext = currentContexts.includes(ctx);
                              let newContexts;
                              if (hasContext) {
                                // Remove context (but keep at least one)
                                newContexts = currentContexts.filter(c => c !== ctx);
                                if (newContexts.length === 0) newContexts = ['round'];
                              } else {
                                // Add context
                                newContexts = [...currentContexts, ctx];
                              }
                              updateScoringCategory(idx, 'contexts', newContexts);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}
                <Grid item xs="auto">
                  <IconButton onClick={() => removeScoringCategory(idx)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
              {/* Matrix Layout Fields (for popup entry style) */}
              <Box sx={{ mt: 1, pl: 4, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="Row Label"
                  value={category.rowLabel || ''}
                  onChange={(e) => updateScoringCategory(idx, 'rowLabel', e.target.value)}
                  size="small"
                  sx={{ width: 150 }}
                  placeholder="e.g., Trees"
                  helperText="Matrix row group"
                />
                <TextField
                  label="Column Label"
                  value={category.columnLabel || ''}
                  onChange={(e) => updateScoringCategory(idx, 'columnLabel', e.target.value)}
                  size="small"
                  sx={{ width: 150 }}
                  placeholder="e.g., 1-High"
                  helperText="Matrix column header"
                />
                {category.rowLabel && category.columnLabel && (
                  <Chip
                    label="M"
                    size="small"
                    color="secondary"
                    title="Matrix layout configured"
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Popup mode: categories with same Row Label are grouped into matrix rows
                </Typography>
              </Box>
              {/* Tap-to-Add (for event categories) */}
              {category.contexts?.includes('event') && (
                <Box sx={{ mt: 1, pl: 4, display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={category.tapToAdd || false}
                        onChange={(e) => updateScoringCategory(idx, 'tapToAdd', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Tap to Add"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Tap adds event directly without data entry (uses formula with x=1)
                  </Typography>
                </Box>
              )}
            </Box>
          ))}

          {/* Drop zone for dragging to end of list */}
          {scoringCategories.length > 0 && (
            <Box
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, scoringCategories.length)}
              sx={{
                p: 2,
                mb: 2,
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 1,
                textAlign: 'center',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
            >
              Drop here to move to end
            </Box>
          )}

          <Button
            startIcon={<AddIcon />}
            onClick={addScoringCategory}
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add Scoring Category
          </Button>
        </Paper>

        {/* Tie-Breaker Configuration - for score, multiRounds, multiRounds+, streamEvents */}
        {(scoringType === SCORING_TYPES.SCORE ||
          scoringType === SCORING_TYPES.MULTI_ROUNDS ||
          scoringType === SCORING_TYPES.MULTI_ROUNDS_PLUS ||
          scoringType === SCORING_TYPES.STREAM_EVENTS) && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>Tie-Breaker Configuration</Typography>
            <Divider sx={{ mb: 2 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={tieBreakerConfig.enabled}
                  onChange={(e) => setTieBreakerConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                />
              }
              label="Enable Tie-Breaker"
            />

            {tieBreakerConfig.enabled && (
              <>
                <Alert severity="info" sx={{ my: 2 }}>
                  Tie-breaker groups are evaluated in order until a tie is broken. Each group can have multiple questions.
                </Alert>

                {tieBreakerConfig.groups.map((group, groupIdx) => (
                  <Paper key={group.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Group {groupIdx + 1}: {group.title || '(Untitled)'}
                      </Typography>
                      <IconButton size="small" onClick={() => removeTieBreakerGroup(groupIdx)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Group Title"
                          value={group.title}
                          onChange={(e) => updateTieBreakerGroup(groupIdx, 'title', e.target.value)}
                          fullWidth
                          size="small"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Win Condition</InputLabel>
                          <Select
                            value={group.winCondition}
                            onChange={(e) => updateTieBreakerGroup(groupIdx, 'winCondition', e.target.value)}
                            label="Win Condition"
                          >
                            <MenuItem value="high">Highest Wins</MenuItem>
                            <MenuItem value="low">Lowest Wins</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Questions</Typography>

                    {group.questions.map((question, qIdx) => (
                      <Box key={question.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <TextField
                          label="Title"
                          value={question.title}
                          onChange={(e) => updateTieBreakerQuestion(groupIdx, qIdx, 'title', e.target.value)}
                          size="small"
                          sx={{ flex: 2 }}
                          required
                        />
                        <TextField
                          label="Formula"
                          value={question.formula}
                          onChange={(e) => updateTieBreakerQuestion(groupIdx, qIdx, 'formula', e.target.value)}
                          size="small"
                          sx={{ flex: 1 }}
                          placeholder="*1"
                          helperText="Use @categoryId for sums"
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeTieBreakerQuestion(groupIdx, qIdx)}
                          disabled={group.questions.length <= 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}

                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addTieBreakerQuestion(groupIdx)}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Add Question
                    </Button>
                  </Paper>
                ))}

                <Button
                  startIcon={<AddIcon />}
                  onClick={addTieBreakerGroup}
                  variant="outlined"
                  sx={{ mt: 1 }}
                >
                  Add Tie-Breaker Group
                </Button>

                {tieBreakerConfig.groups.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Evaluation Order:</Typography>
                    {tieBreakerConfig.groups.map((group, idx) => (
                      <Typography key={group.id} variant="body2" color="text.secondary">
                        {idx + 1}. {group.title || '(Untitled)'} → {group.winCondition === 'high' ? 'Highest wins' : 'Lowest wins'}
                      </Typography>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Paper>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/games')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {isEditMode ? 'Update Game' : 'Create Game'}
          </Button>
        </Box>
      </form>

      {/* Contributor Selection Dialog */}
      <Dialog
        open={contributorDialogOpen}
        onClose={() => setContributorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Contributor</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            placeholder="Search by handle or email..."
            value={contributorSearch}
            onChange={(e) => setContributorSearch(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          {contributorsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {contributorsData?.contributors?.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No contributors found"
                    secondary="Try a different search term"
                  />
                </ListItem>
              ) : (
                contributorsData?.contributors?.map((contributor) => (
                  <ListItem key={contributor.id} disablePadding divider>
                    <ListItemButton
                      selected={selectedContributor.id === contributor.id}
                      onClick={() => {
                        setSelectedContributor({
                          id: contributor.id,
                          handle: contributor.handle
                        });
                        setContributorDialogOpen(false);
                        setContributorSearch('');
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1">{contributor.handle}</Typography>
                            <Chip label={`ID: ${contributor.id}`} size="small" variant="outlined" />
                          </Box>
                        }
                        secondary={contributor.contact_email}
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setContributorDialogOpen(false);
            setContributorSearch('');
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
